import React from "react";
import { useAppSelector, useAppDispatch } from "../redux/hooks";

import { ReactComponent as IconWarning } from "../assets/Icons/Icon_Warning.svg";
import { ReactComponent as IconInfo } from "../assets/Icons/circle-info-solid.svg";
import { ReactComponent as IconCross } from "../assets/Icons/CrossIcon.svg";
import { dismissNotice } from "@redux/slices/tenantNoticesSlice";
import {
  SkeletonCircle,
  SkeletonLine,
} from "../../ui-components/Skeleton/GenericSkeleton";

const TenantNoticeBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const { notice, dismissed, isLoading } = useAppSelector(
    (state) => state.tenantNotices
  );

  // Skeleton when it's loading
  if (isLoading) {
    return (
      <div
        className="
            bg-[var(--color-primitive-gray-50)]
            border-b
            border-[var(--color-primitive-gray-200)]
            px-4
            py-3
            w-full
            flex
            items-center
            gap-3
            z-25
        "
        role="status"
        aria-label="Loading tenant notices"
      >
        <SkeletonCircle size={20} className="flex-shrink-0" />
        <SkeletonLine w="60%" h={16} className="flex-1" />
      </div>
    );
  }

  // Don't show if there's no notification, was dismissed or isn't active
  if (!notice || !notice.active || dismissed) {
    return null;
  }

  //Dismissible types
  const dismissibleTypes = ["info", "notice", "success"];
  const isDismissible = dismissibleTypes.includes(notice.type.toLowerCase());

  //Styles according types
  let containerBg = "bg-[var(--color-semantic-bg-brand-secondary)]";
  let textColor = "text-[var(--color-semantic-text-brand)]";
  let borderColor = "border-[var(--color-primitive-brand-surf-600)]";
  let IconComp = IconInfo;

  const type = notice.type.toLowerCase();

  if (type === "warning") {
    containerBg = "bg-[var(--color-semantic-bg-warning-secondary)]";
    textColor = "text-[var(--color-semantic-text-warning)]";
    borderColor = "border-[var(--color-utility-warning-600)]";
    IconComp = IconWarning;
  } else if (type === "error" || type === "critical") {
    containerBg = "bg-[var(--color-semantic-bg-danger-secondary)]";
    textColor = "text-[var(--color-semantic-text-danger)]";
    borderColor = "border-[var(--color-utility-danger-600)]";
    IconComp = IconWarning;
  }

  const handleDismiss = () => {
    if (isDismissible) {
      dispatch(dismissNotice());
    }
  };

  return (
    <div
      className={`
        ${containerBg}
        ${textColor}
        ${borderColor}
        border-b
        px-4
        py-3
        w-full
        flex
        items-center
        justify-between
        gap-4
        z-15
      `}
      role="alert"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <IconComp className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm font-medium flex-1">{notice.message}</p>
      </div>

      {isDismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          className={`
            ${textColor}
            hover:opacity-70
            transition-opacity
            flex-shrink-0
            p-1
            rounded-sm
            focus:outline-none
            focus:ring-2
            focus:ring-offset-2
            focus:ring-current
          `}
          aria-label="Dismiss notice"
        >
          <IconCross className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default TenantNoticeBanner;
